<template>
  <div class="dashboard">
    <base-navigation></base-navigation>

    <!-- HEADER WITH GREETING AND MONTH SELECTOR -->
    <div class="header-section">
      <div>
        <h1 class="page-title">Monthly Loan Analysis</h1>
        <p class="page-subtitle">Performance overview for {{ selectedMonthName }} {{ selectedYear }}</p>
      </div>
      <div class="month-selector">
        <button @click="previousMonth" class="month-nav-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <span class="current-month">{{ selectedMonthName }} {{ selectedYear }}</span>
        <button @click="nextMonth" class="month-nav-btn" :disabled="isCurrentMonth">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>

    <!-- MONTHLY SUMMARY CARDS -->
    <div class="cards-grid">
      <div class="stat-card clients">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">New Clients</span>
          <span class="stat-value">{{ monthlyStats.newClients }}</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 6 13.5 15.5 8 10 1 17"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
            Registered this month
          </span>
        </div>
      </div>

      <div class="stat-card loans">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="6" width="20" height="12" rx="2"></rect>
            <path d="M12 12h.01"></path>
            <path d="M22 6l-10 7L2 6"></path>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Loans Disbursed</span>
          <span class="stat-value">{{ monthlyStats.loanCount }}</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
            {{ formatCurrency(monthlyStats.disbursedAmount) }} total
          </span>
        </div>
      </div>

      <div class="stat-card payments">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
            <path d="M12 18V6"></path>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Payments Received</span>
          <span class="stat-value">{{ monthlyStats.paymentCount }}</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
            {{ formatCurrency(monthlyStats.paymentAmount) }} total
          </span>
        </div>
      </div>

      <div class="stat-card interest">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="20" x2="12" y2="10"></line>
            <line x1="18" y1="20" x2="18" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="16"></line>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Interest Collected</span>
          <span class="stat-value">{{ formatCurrency(monthlyStats.interestCollected) }}</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            From {{ monthlyStats.interestCount }} payments
          </span>
        </div>
      </div>
    </div>

    <!-- ADDITIONAL MONTHLY METRICS -->
    <div class="cards-grid">
      <div class="stat-card average">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 20L22 20"></path>
            <path d="M4 16L8 8 12 12 16 6 20 10"></path>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Average Loan Size</span>
          <span class="stat-value">{{ formatCurrency(monthlyStats.averageLoan) }}</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
            Per loan this month
          </span>
        </div>
      </div>

      <div class="stat-card collection">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Collection Rate</span>
          <span class="stat-value">{{ monthlyStats.collectionRate }}%</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Principal collected / disbursed
          </span>
        </div>
      </div>

      <div class="stat-card active">
        <div class="stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Active Loans</span>
          <span class="stat-value">{{ monthlyStats.activeLoans }}</span>
          <span class="stat-footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            Currently being repaid
          </span>
        </div>
      </div>
    </div>

    <!-- QUICK ACTIONS -->
    <div class="actions-section">
      <h2 class="section-title">Quick Actions</h2>
      <div class="actions-grid">
        <router-link to="/register-client" class="action-card">
          <div class="action-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <line x1="19" y1="8" x2="19" y2="14"></line>
              <line x1="22" y1="11" x2="16" y2="11"></line>
            </svg>
          </div>
          <span class="action-label">Register Client</span>
          <span class="action-hint">Add new client</span>
        </router-link>

        <router-link to="/create-loan" class="action-card">
          <div class="action-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="6" width="20" height="12" rx="2"></rect>
              <line x1="12" y1="12" x2="12" y2="18"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
          </div>
          <span class="action-label">Create Loan</span>
          <span class="action-hint">Issue new loan</span>
        </router-link>

        <router-link to="/monthly-report" class="action-card">
          <div class="action-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </div>
          <span class="action-label">Monthly Report</span>
          <span class="action-hint">View details</span>
        </router-link>

        <router-link to="/clients-list" class="action-card">
          <div class="action-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <span class="action-label">View Clients</span>
          <span class="action-hint">{{ clients.length }} total</span>
        </router-link>
      </div>
    </div>

    <!-- MONTHLY LOANS LIST -->
    <div class="recent-section">
      <div class="section-header">
        <h2 class="section-title">Loans Disbursed in {{ selectedMonthName }} {{ selectedYear }}</h2>
        <span class="record-count">{{ monthlyStats.loanCount }} loans</span>
      </div>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Loan Amount</th>
              <th>Interest</th>
              <th>Total Repayment</th>
              <th>Paid to Date</th>
              <th>Current Balance</th>
              <th>Status</th>
              <th>Disbursement Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="loan in monthlyLoans" :key="loan.id" class="loan-row">
              <td>
                <div class="client-cell">
                  <div class="client-avatar">{{ getInitials(loan.client_name) }}</div>
                  <span>{{ loan.client_name }}</span>
                </div>
              </td>
              <td>
                <span class="amount-primary">{{ formatCurrency(loan.loan_amount) }}</span>
              </td>
              <td>
                <span class="amount-interest">{{ formatCurrency(loan.interest_balance) }}</span>
              </td>
              <td>
                <span class="amount-total">{{ formatCurrency(loan.total_repayment) }}</span>
              </td>
              <td>
                <span class="amount-paid">{{ formatCurrency(loan.total_paid) }}</span>
              </td>
              <td>
                <span :class="['balance-chip', getBalanceClass(loan)]">
                  {{ formatCurrency(loan.loan_balance) }}
                </span>
              </td>
              <td>
                <span :class="['status-chip', getStatusClass(loan)]">
                  {{ getStatusText(loan) }}
                </span>
              </td>
              <td>
                <div class="date-cell">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  {{ formatDate(loan.createdAt) }}
                </div>
              </td>
            </tr>
            <tr v-if="monthlyLoans.length === 0">
              <td colspan="8" class="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>No loans disbursed in this month</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MONTHLY PAYMENTS LIST -->
    <div class="recent-section" style="margin-top: 24px;">
      <div class="section-header">
        <h2 class="section-title">Payments Received in {{ selectedMonthName }} {{ selectedYear }}</h2>
        <span class="record-count">{{ monthlyStats.paymentCount }} payments</span>
      </div>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Customer</th>
              <th>Loan ID</th>
              <th>Total Amount</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>Payment Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in monthlyPayments" :key="payment.id" class="payment-row">
              <td>
                <span class="transaction-id">{{ payment.TransID }}</span>
              </td>
              <td>
                <div class="client-cell">
                  <div class="client-avatar small">{{ getInitials(payment.customer_name) }}</div>
                  <span>{{ payment.customer_name }}</span>
                </div>
              </td>
              <td>
                <span class="loan-id">#{{ payment.loan_id }}</span>
              </td>
              <td>
                <span class="amount-primary">{{ formatCurrency(payment.TransAmount) }}</span>
              </td>
              <td>
                <span class="amount-principal">{{ formatCurrency(payment.principal) }}</span>
              </td>
              <td>
                <span class="amount-interest">{{ formatCurrency(payment.interest) }}</span>
              </td>
              <td>
                <div class="date-cell">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  {{ formatDate(payment.payment_date) }}
                </div>
              </td>
            </tr>
            <tr v-if="monthlyPayments.length === 0">
              <td colspan="7" class="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>No payments received in this month</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "Dashboard",

  data() {
    return {
      clients: [],
      loans: [],
      payments: [],
      selectedMonth: new Date().getMonth(),
      selectedYear: new Date().getFullYear(),
      monthlyStats: {
        newClients: 0,
        loanCount: 0,
        disbursedAmount: 0,
        paymentCount: 0,
        paymentAmount: 0,
        interestCollected: 0,
        interestCount: 0,
        averageLoan: 0,
        collectionRate: 0,
        activeLoans: 0
      },
      monthlyLoans: [],
      monthlyPayments: [],
      currentDate: new Date().toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    };
  },

  computed: {
    selectedMonthName() {
      return new Date(this.selectedYear, this.selectedMonth).toLocaleDateString('en-US', { month: 'long' });
    },
    isCurrentMonth() {
      const now = new Date();
      return this.selectedMonth === now.getMonth() && this.selectedYear === now.getFullYear();
    }
  },

  async mounted() {
    await this.loadClients();
    await this.loadLoans();
    await this.loadPayments();
    this.updateMonthlyAnalysis();
  },

  methods: {
    async loadClients() {
      try {
        const response = await axios.get(
          "https://api.roberms.com/raisetrack/get/loan/clients"
        );
        this.clients = response.data;
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },

    async loadLoans() {
      try {
        const response = await axios.get(
          "https://api.roberms.com/raisetrack/get/loans"
        );
        this.loans = response.data.map(loan => ({
          ...loan,
          total_repayment: this.calculateTotalRepayment(loan)
        }));
      } catch (error) {
        console.error("Error loading loans:", error);
      }
    },

    async loadPayments() {
      try {
        const response = await axios.get(
          "https://api.roberms.com/raisetrack/get/loan/payments"
        );
        this.payments = response.data;
      } catch (error) {
        console.error("Error loading payments:", error);
      }
    },

    previousMonth() {
      if (this.selectedMonth === 0) {
        this.selectedMonth = 11;
        this.selectedYear -= 1;
      } else {
        this.selectedMonth -= 1;
      }
      this.updateMonthlyAnalysis();
    },

    nextMonth() {
      if (!this.isCurrentMonth) {
        if (this.selectedMonth === 11) {
          this.selectedMonth = 0;
          this.selectedYear += 1;
        } else {
          this.selectedMonth += 1;
        }
        this.updateMonthlyAnalysis();
      }
    },

    updateMonthlyAnalysis() {
      this.calculateMonthlyClients();
      this.calculateMonthlyLoans();
      this.calculateMonthlyPayments();
      this.calculateAdditionalMetrics();
    },

    calculateMonthlyClients() {
      this.monthlyStats.newClients = this.clients.filter(client => {
        const clientDate = new Date(client.createdAt);
        return clientDate.getMonth() === this.selectedMonth && 
               clientDate.getFullYear() === this.selectedYear;
      }).length;
    },

    calculateMonthlyLoans() {
      this.monthlyLoans = this.loans.filter(loan => {
        const loanDate = new Date(loan.createdAt);
        return loanDate.getMonth() === this.selectedMonth && 
               loanDate.getFullYear() === this.selectedYear;
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      this.monthlyStats.loanCount = this.monthlyLoans.length;
      this.monthlyStats.disbursedAmount = this.monthlyLoans.reduce(
        (sum, loan) => sum + parseFloat(loan.loan_amount || 0), 0
      );
      
      this.monthlyStats.averageLoan = this.monthlyStats.loanCount > 0 
        ? this.monthlyStats.disbursedAmount / this.monthlyStats.loanCount 
        : 0;
    },

    calculateMonthlyPayments() {
      this.monthlyPayments = this.payments.filter(payment => {
        const paymentDate = new Date(payment.payment_date);
        return paymentDate.getMonth() === this.selectedMonth && 
               paymentDate.getFullYear() === this.selectedYear;
      }).sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));

      this.monthlyStats.paymentCount = this.monthlyPayments.length;
      this.monthlyStats.paymentAmount = this.monthlyPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.TransAmount || 0), 0
      );
      
      this.monthlyStats.interestCollected = this.monthlyPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.interest || 0), 0
      );
      
      this.monthlyStats.interestCount = this.monthlyPayments.filter(
        payment => parseFloat(payment.interest || 0) > 0
      ).length;
    },

    calculateAdditionalMetrics() {
      // Collection rate (principal collected / disbursed)
      const principalCollected = this.monthlyPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.principal || 0), 0
      );
      
      this.monthlyStats.collectionRate = this.monthlyStats.disbursedAmount > 0
        ? Math.round((principalCollected / this.monthlyStats.disbursedAmount) * 100)
        : 0;

      // Active loans from this month's disbursements
      this.monthlyStats.activeLoans = this.monthlyLoans.filter(
        loan => parseFloat(loan.loan_balance) > 0
      ).length;
    },

    calculateTotalRepayment(loan) {
      return parseFloat(loan.loan_amount || 0) + parseFloat(loan.interest_balance || 0);
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getInitials(name) {
      if (!name) return 'NA';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },

    getBalanceClass(loan) {
      const balance = parseFloat(loan.loan_balance);
      const amount = parseFloat(loan.loan_amount);
      const ratio = balance / amount;
      
      if (balance === 0) return 'paid';
      if (ratio < 0.3) return 'low';
      if (ratio < 0.7) return 'medium';
      return 'high';
    },

    getStatusClass(loan) {
      const balance = parseFloat(loan.loan_balance);
      return balance === 0 ? 'completed' : 'active';
    },

    getStatusText(loan) {
      const balance = parseFloat(loan.loan_balance);
      return balance === 0 ? 'Paid' : 'Active';
    }
  },

  watch: {
    clients: {
      handler() {
        this.updateMonthlyAnalysis();
      },
      deep: true
    },
    loans: {
      handler() {
        this.updateMonthlyAnalysis();
      },
      deep: true
    },
    payments: {
      handler() {
        this.updateMonthlyAnalysis();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.dashboard {
  padding: 24px;
  background: #f3f4f6;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Header Section */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  background: white;
  padding: 20px 24px;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.month-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 100px;
}

.month-nav-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.month-nav-btn:hover:not(:disabled) {
  background: #3b82f6;
  color: white;
  transform: scale(1.05);
}

.month-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.month-nav-btn:last-child svg {
  transform: rotate(180deg);
}

.current-month {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  min-width: 140px;
  text-align: center;
}

/* Cards Grid */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: all 0.2s ease;
  border: 1px solid rgba(0,0,0,0.03);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
}

.stat-card.clients .stat-icon {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.stat-card.loans .stat-icon {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-card.payments .stat-icon {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
}

.stat-card.interest .stat-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-card.average .stat-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.stat-card.collection .stat-icon {
  background: linear-gradient(135deg, #ec4899, #db2777);
}

.stat-card.active .stat-icon {
  background: linear-gradient(135deg, #6b7280, #4b5563);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

.stat-value {
  display: block;
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
  line-height: 1.2;
}

.stat-footer {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.stat-footer svg {
  stroke: #9ca3af;
}

/* Actions Section */
.actions-section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
  letter-spacing: -0.3px;
}

.record-count {
  font-size: 14px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 12px;
  border-radius: 100px;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.action-card {
  background: white;
  border-radius: 14px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.03);
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  background: #f9fafb;
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  margin-bottom: 12px;
}

.action-label {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.action-hint {
  font-size: 12px;
  color: #9ca3af;
}

/* Recent Sections */
.recent-section {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-container {
  overflow-x: auto;
  border-radius: 12px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: left;
  padding: 14px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  letter-spacing: 0.3px;
}

td {
  padding: 16px;
  font-size: 14px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
}

.loan-row:hover, .payment-row:hover {
  background: #f9fafb;
}

.client-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.client-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
}

.client-avatar.small {
  width: 28px;
  height: 28px;
  font-size: 11px;
}

.amount-primary {
  font-weight: 600;
  color: #111827;
}

.amount-interest {
  font-weight: 500;
  color: #f59e0b;
}

.amount-total {
  font-weight: 600;
  color: #8b5cf6;
}

.amount-paid {
  font-weight: 500;
  color: #10b981;
}

.amount-principal {
  font-weight: 500;
  color: #3b82f6;
}

.transaction-id {
  font-family: monospace;
  font-size: 12px;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
  color: #4b5563;
}

.loan-id {
  font-weight: 600;
  color: #6b7280;
}

.balance-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 100px;
  font-weight: 500;
  font-size: 13px;
}

.balance-chip.paid {
  background: #d1fae5;
  color: #065f46;
}

.balance-chip.low {
  background: #dbeafe;
  color: #1e40af;
}

.balance-chip.medium {
  background: #fed7aa;
  color: #92400e;
}

.balance-chip.high {
  background: #fee2e2;
  color: #991b1b;
}

.status-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 100px;
  font-weight: 500;
  font-size: 13px;
}

.status-chip.active {
  background: #dbeafe;
  color: #1e40af;
}

.status-chip.completed {
  background: #d1fae5;
  color: #065f46;
}

.date-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 13px;
}

.date-cell svg {
  stroke: #9ca3af;
}

.empty-state {
  text-align: center;
  padding: 48px !important;
  color: #9ca3af;
}

.empty-state svg {
  stroke: #d1d5db;
  margin-bottom: 12px;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 640px) {
  .dashboard {
    padding: 16px;
  }

  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }

  .page-title {
    font-size: 20px;
  }

  .month-selector {
    width: 100%;
    justify-content: space-between;
  }

  .current-month {
    min-width: auto;
    font-size: 13px;
  }

  .actions-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .action-card {
    padding: 14px 12px;
  }

  .action-icon {
    width: 40px;
    height: 40px;
  }

  .action-label {
    font-size: 13px;
  }

  .action-hint {
    display: none;
  }

  th, td {
    padding: 12px 8px;
    font-size: 13px;
  }

  .client-avatar {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }

  .balance-chip, .status-chip {
    padding: 2px 8px;
    font-size: 12px;
  }
}
</style>